import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ClinicalTaskApprovalPreviewTask } from '../../../contracts/projects/clinical-task-approval-preview';
import { parseClinicalTaskApprovalTasks } from '../../../contracts/projects/clinical-task-approval-preview';
import type { ApprovalRequestInput } from './ApprovalRequestBlock';
import { InlineApprovalBanner } from './inline-approval/inline-approval-banner';
import type { ResolveInlineApproval } from './inline-approval/useInlineApprovalResolve';
import { useInlineApprovalResolve } from './inline-approval/useInlineApprovalResolve';

interface ClinicalTaskApprovalBlockProps {
  approval: ApprovalRequestInput;
  onResolve: ResolveInlineApproval;
  /**
   * The proposed tasks' row. The composition point renders the clinical-task
   * tool row in its pre-approval ``proposed`` mode; that row is Evidence's.
   */
  renderTaskPreview: (preview: {
    tasks: ClinicalTaskApprovalPreviewTask[];
    title: string;
  }) => ReactNode;
}

/**
 * HITL approval for ``create_clinical_task``. Renders the same slim
 * ``ClinicalTaskToolBlock`` tool row that appears after creation (in its
 * pre-approval ``proposed`` mode — collapsible task preview + "View all")
 * stacked directly above a slim approve / cancel banner. This keeps the
 * approval visually continuous with the live tool row. No inline editing —
 * approving sends the original tool input unchanged; the tasks are
 * materialised by the agent on the backend.
 */
export const ClinicalTaskApprovalBlock = ({
  approval,
  onResolve,
  renderTaskPreview,
}: ClinicalTaskApprovalBlockProps) => {
  const intl = useIntl();
  const tasks = useMemo(
    () => parseClinicalTaskApprovalTasks(approval.tool_input, approval.approval_id),
    [approval.tool_input, approval.approval_id]
  );
  const { pendingVerdict, isBusy, submit } = useInlineApprovalResolve({
    approvalId: approval.approval_id,
    onResolve,
  });

  if (!tasks) {
    return null;
  }

  const bannerTitle = intl.formatMessage(
    {
      defaultMessage:
        'Heidi wants to create {count, plural, one {# clinical task} other {# clinical tasks}}',
      id: '4hNLg5Vh8+',
    },
    { count: tasks.length }
  );

  const rowTitle = intl.formatMessage({
    defaultMessage: 'Create clinical tasks',
    id: 'TMJ9UqNGd3',
  });

  return (
    <div
      className="flex w-full flex-col gap-2"
      data-testid={`clinical-task-approval-${approval.approval_id}`}
    >
      {renderTaskPreview({ tasks, title: rowTitle })}
      <InlineApprovalBanner
        title={bannerTitle}
        onApprove={() => submit('approve')}
        onDeny={() => submit('deny')}
        pendingVerdict={pendingVerdict}
        disabled={isBusy}
      />
    </div>
  );
};
